import React from 'react';

import { useAuth } from '@/state/auth';

import {
  AuthenticationSettingsSubsection,
  ProfileSettingsSubsection,
} from './subsections';

/**
 * UserProfileCards displays personal profile and authentication settings.
 *
 * CRITICAL: This component IGNORES impersonation.
 * It ALWAYS shows the logged-in user's own profile settings.
 *
 * Rationale:
 * - Profile settings are always "MY settings" - no ambiguity
 * - Proper RBAC needs implementation before allowing profile viewing of others
 * - AccountDropdown "User Profile" link should always show own profile
 * - Security: Prevents accidental modification of other users' profiles
 *
 * Subsections:
 * - Profile Settings: User identity and appearance (profile picture, display name, theme)
 * - Authentication Settings: Security and access control (auth method, password, MFA, API keys)
 *
 * Cards:
 * Profile Settings:
 * 1. User Profile - Profile picture upload and display name editing
 * 2. Interface Theme - Theme selection (system/dark/light)
 *
 * Authentication Settings:
 * 3. Authentication Method - SSO vs Local authentication display
 * 4. Password - Password reset (local auth only)
 * 5. Two-Factor Authentication - MFA enrollment (local auth only)
 * 6. API Keys - Personal API key management
 *
 * Pattern Compliance:
 * - Two subsection headers for logical grouping (h3)
 * - Single unified card layout with gap-2 spacing within subsections
 * - Modal-based editing (no inline EditableField)
 * - Complete data-testid coverage for E2E testing
 *
 * React 19 Features:
 * - Standard caching strategy with TanStack Query
 * - No unnecessary memoization (React Compiler handles optimization)
 *
 * @see {@link UserManagementCards} - For user management (respects impersonation)
 * @see {@link Profile} - Separate page that renders this component (accessible from AccountDropdown)
 */
export const UserProfileCards: React.FC = () => {
  // CRITICAL: Always use 'me' - this component ignores impersonation
  const { me, ssoDomain } = useAuth();

  return (
    <div className="flex flex-col gap-2" data-testid="user-profile-cards">
      <ProfileSettingsSubsection me={me} />
      <AuthenticationSettingsSubsection me={me} ssoDomain={ssoDomain} />
    </div>
  );
};
